import { useRef, useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Camera, Close } from '@carbon/icons-react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import {
  BaseLayout,
  SurveyThankYou,
  ImagePreviewModal,
  CameraCapture,
  Spinner,
} from '@/components';
import { Card, CardContent, Button, Label, Textarea } from '@/components/ui';
import { TOAST_TYPES } from '@/constants/common.constants';
import { useToast } from '@/contexts/toast-context';
import { createIncidentReport } from '@/api/incident-report.api';
import heic2any from 'heic2any';

import './incident-reports-add-style.scss';

export const Route = createFileRoute('/incident-reports/add/')({
  component: IncidentReportCreate,
});

function IncidentReportCreate() {
  const { addToast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<Map<File, string>>(new Map());
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoUrlsRef = useRef<Map<File, string>>(new Map());

  // Formik values and schema
  interface IncidentReportFormValues {
    description: string;
    photos: File[];
  }

  const initialValues: IncidentReportFormValues = {
    description: '',
    photos: [],
  };

  const incidentReportSchema = Yup.object().shape({
    description: Yup.string()
      .trim()
      .required('Description is required')
      .min(1, 'Description cannot be empty'),
    photos: Yup.array()
      .of(
        Yup.mixed<File>()
          .required()
          .test('fileType', 'Only JPEG and PNG images are allowed', (value) => {
            if (!value) return false;
            return value.type === 'image/jpeg' || value.type === 'image/png';
          })
          .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value) return false;
            return value.size <= 5 * 1024 * 1024; // 5MB
          }),
      )
      .max(5, 'Maximum 5 photos can be uploaded')
      .default([]),
  });

  // Update ref when photoUrls changes
  useEffect(() => {
    photoUrlsRef.current = photoUrls;
  }, [photoUrls]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      photoUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleAddPhotosClick = () => {
    fileInputRef.current?.click();
  };

  const resizeImageFile = async (file: File): Promise<File | null> => {
    let sourceFile = file;

    // Convert HEIC/HEIF to JPEG first — Chrome on iOS cannot draw HEIC to canvas
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      try {
        const converted = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.85,
        });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        sourceFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
          type: 'image/jpeg',
        });
      } catch {
        return null;
      }
    }

    return new Promise((resolve) => {
      const MAX_WIDTH = 1280;
      const img = new Image();
      const objectUrl = URL.createObjectURL(sourceFile);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const naturalWidth = img.naturalWidth || img.width;
        const naturalHeight = img.naturalHeight || img.height;
        if (!naturalWidth || !naturalHeight) {
          resolve(null);
          return;
        }
        const scale = Math.min(1, MAX_WIDTH / naturalWidth);
        const canvas = document.createElement('canvas');
        canvas.width = naturalWidth * scale;
        canvas.height = naturalHeight * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(
          img,
          0,
          0,
          naturalWidth,
          naturalHeight,
          0,
          0,
          canvas.width,
          canvas.height,
        );
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const resized = new File(
              [blob],
              sourceFile.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg' },
            );
            resolve(resized);
          },
          'image/jpeg',
          0.85,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: File[]) => void,
    currentPhotos: File[],
  ) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      // Filter for valid file types (HEIC/HEIF will be converted to JPEG by resizeImageFile)
      const validFiles = fileArray.filter((file) => {
        return (
          file.type === 'image/jpeg' ||
          file.type === 'image/png' ||
          file.type === 'image/heic' ||
          file.type === 'image/heif'
        );
      });
      if (validFiles.length < fileArray.length) {
        addToast(
          'Some photos were skipped. Only JPEG, PNG, or HEIC images are allowed.',
          { type: TOAST_TYPES.ERROR },
        );
      }
      // Limit to 5 photos total
      const remainingSlots = 5 - currentPhotos.length;
      const filesToProcess = validFiles.slice(0, remainingSlots);

      // Resize all files before adding
      const resizedResults = await Promise.all(
        filesToProcess.map((file) => resizeImageFile(file)),
      );

      // Drop nulls (resize failed) and any still over 5MB
      const filesToAdd = resizedResults.filter((file): file is File => {
        if (!file) {
          addToast('A photo could not be processed and was skipped.', {
            type: TOAST_TYPES.ERROR,
          });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          addToast(
            `${file.name} is too large even after resizing and was skipped.`,
            { type: TOAST_TYPES.ERROR },
          );
          return false;
        }
        return true;
      });

      // Create object URLs for new files
      const newUrls = new Map(photoUrls);
      filesToAdd.forEach((file) => {
        if (!newUrls.has(file)) {
          newUrls.set(file, URL.createObjectURL(file));
        }
      });
      setPhotoUrls(newUrls);

      setFieldValue('photos', [...currentPhotos, ...filesToAdd]);
      // Reset input to allow selecting the same file again
      e.target.value = '';
    }
  };

  const handleCameraCapture = (
    file: File,
    setFieldValue: (field: string, value: File[]) => void,
    currentPhotos: File[],
  ) => {
    // Create object URL for the new file
    const newUrls = new Map(photoUrls);
    if (!newUrls.has(file)) {
      newUrls.set(file, URL.createObjectURL(file));
    }
    setPhotoUrls(newUrls);

    setFieldValue('photos', [...currentPhotos, file]);
  };

  const handleSubmit = async (values: IncidentReportFormValues) => {
    try {
      // Create FormData object with description and photos
      const formData = new FormData();

      // Append description
      formData.append('description', values.description.trim());

      // Append each photo file
      values.photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      // Submit to backend
      const response = await createIncidentReport(formData);
      addToast(response.message || 'Incident report submitted successfully', {
        type: TOAST_TYPES.SUCCESS,
      });

      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Failed to submit incident report',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
    }
  };

  if (isSubmitted) {
    return (
      <BaseLayout
        header={{
          headerText: 'Incident Report',
        }}
      >
        <SurveyThankYou message="Your incident report has been submitted successfully. It will be routed to your manager for review." />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout
      header={{
        headerText: 'Incident Report',
      }}
    >
      <Card className="incident-report-add-card">
        <CardContent>
          <h2 className="incident-report-add-heading">
            Submit your incident report below
          </h2>

          <Formik
            initialValues={initialValues}
            validationSchema={incidentReportSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              setFieldValue,
              setFieldTouched,
              isSubmitting,
              errors,
              touched,
            }) => (
              <>
                <Form>
                  <div className="form-container">
                    <div>
                      <Label htmlFor="description">Describe the incident</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={values.description}
                        onChange={(e) =>
                          setFieldValue('description', e.target.value)
                        }
                        onBlur={() => setFieldTouched('description', true)}
                        placeholder="Enter your description..."
                        rows={6}
                        className={
                          errors.description && touched.description
                            ? 'is-invalid'
                            : ''
                        }
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div>
                      <div className="incident-report-add-photo-actions">
                        <Button
                          type="button"
                          variant="basic-secondary"
                          size="medium"
                          className="incident-report-add-photo-button"
                          onClick={handleAddPhotosClick}
                          disabled={values.photos.length >= 5}
                        >
                          <Camera className="medium-icon" />
                          Add Photo(s)
                        </Button>
                        <Button
                          type="button"
                          variant="basic-secondary"
                          size="medium"
                          className="incident-report-add-photo-button"
                          onClick={() => setIsCameraOpen(true)}
                          disabled={values.photos.length >= 5}
                        >
                          <Camera className="medium-icon" />
                          Take Photo
                        </Button>
                      </div>
                      <div className="incident-report-add-photo-info mt-2">
                        <p className="help-text">
                          Max 5 photos, 5MB per photo. JPG, PNG accepted.
                        </p>
                      </div>
                      {values.photos.length > 0 && (
                        <div className="incident-report-add-photos-section">
                          <Label
                            className={`mt-2${
                              values.photos.length >= 5
                                ? ' text-danger-dark-1'
                                : ''
                            }`}
                          >
                            Added Photos {values.photos.length}/5
                          </Label>
                          {values.photos.length >= 5 && (
                            <p className="text-danger-dark-1 mt-1">
                              You can upload up to 5 photos. Please remove one
                              to continue.
                            </p>
                          )}
                          <div className="incident-report-add-photos-gallery">
                            {values.photos.map((photo: File, index: number) => {
                              const url = photoUrls.get(photo);
                              return (
                                <div
                                  key={`${photo.name}-${index}`}
                                  className="incident-report-photo-item"
                                >
                                  <div className="incident-report-photo-wrapper">
                                    <img
                                      src={url}
                                      alt={`Photo ${index + 1}`}
                                      className="incident-report-photo-image"
                                      onClick={() => {
                                        if (url) {
                                          setPreviewImage(url);
                                        }
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      className="incident-report-photo-remove"
                                      onClick={() => {
                                        // Revoke object URL when removing photo
                                        if (url) {
                                          URL.revokeObjectURL(url);
                                        }
                                        const newUrls = new Map(photoUrls);
                                        newUrls.delete(photo);
                                        setPhotoUrls(newUrls);

                                        const updatedPhotos =
                                          values.photos.filter(
                                            (_: File, i: number) => i !== index,
                                          );
                                        setFieldValue('photos', updatedPhotos);
                                      }}
                                    >
                                      <Close className="small-icon" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Hidden file input - triggered programmatically by "Add Photo(s)" button */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/heic,image/heif"
                        multiple
                        onChange={(e) =>
                          handleFileChange(e, setFieldValue, values.photos)
                        }
                        style={{ display: 'none' }}
                      />
                    </div>
                    <CameraCapture
                      isOpen={isCameraOpen}
                      onClose={() => setIsCameraOpen(false)}
                      onCapture={(file) =>
                        handleCameraCapture(file, setFieldValue, values.photos)
                      }
                      maxPhotos={5}
                      currentPhotoCount={values.photos.length}
                    />

                    <div>
                      <div className="button-container-right">
                        <Button
                          type="submit"
                          variant="basic-primary"
                          size="medium"
                          className="incident-report-add-submit-button"
                          disabled={isSubmitting}
                        >
                          {isSubmitting && <Spinner size="small" />}
                          Submit
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
                <ImagePreviewModal
                  imageUrl={previewImage}
                  onClose={() => setPreviewImage(null)}
                />
              </>
            )}
          </Formik>
        </CardContent>
      </Card>
    </BaseLayout>
  );
}
